import { Button, Page, TextField } from '../components/forms';
/**
 * ORGANIZATION SETTINGS PAGE
 *
 * Manage organization details, auth settings, and team members.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Banner,
  Tabs,
  Box,
  Divider,
} from '@shopify/polaris';
import { useAuth } from '../App';
import { CompanyAuthPanel } from '../components/company/CompanyAuthPanel';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.whenlyapp.com';

interface OrganizationDetails {
  id: string;
  name: string;
  slug: string;
  memberCount: number;
  shopCount: number;
  createdAt: string;
}

export default function OrganizationSettings() {
  const { currentOrganization } = useAuth();
  return <OrganizationSettingsPage key={currentOrganization?.id || 'none'} />;
}
function OrganizationSettingsPage() {
  const navigate = useNavigate();
  const { currentOrganization, refreshAuth } = useAuth();

  const [selectedTab, setSelectedTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Organization details
  const [org, setOrg] = useState<OrganizationDetails | null>(null);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');

  useEffect(() => {
    if (currentOrganization) {
      loadOrganization();
    }
  }, [currentOrganization]);

  const loadOrganization = async () => {
    if (!currentOrganization) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/organizations/${currentOrganization.id}`, {
        credentials: 'include',
        headers: { 'X-Organization-Id': currentOrganization.id },
      });

      const result = await response.json();

      if (!result.success) {
        setError(result.error || 'Failed to load organization');
        return;
      }

      const orgData = result.organization;
      setOrg(orgData);
      setName(orgData.name);
      setSlug(orgData.slug);
    } catch {
      setError('Failed to load organization');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveGeneral = useCallback(async () => {
    if (!currentOrganization) return;

    setError(null);
    setSuccess(null);
    setIsSaving(true);

    try {
      const response = await fetch(`${API_URL}/api/organizations/${currentOrganization.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-Organization-Id': currentOrganization.id },
        credentials: 'include',
        body: JSON.stringify({ name }),
      });

      const result = await response.json();

      if (!result.success) {
        setError(result.error || 'Failed to update organization');
        return;
      }

      setSuccess('Organization updated successfully');
      await refreshAuth();
    } catch {
      setError('Failed to update organization');
    } finally {
      setIsSaving(false);
    }
  }, [currentOrganization, name, slug, refreshAuth]);

  const tabs = [
    { id: 'general', content: 'General' },
    { id: 'auth', content: 'Authentication' },
    { id: 'team', content: 'Team Members' },
  ];

  if (!currentOrganization) {
    return (
      <Page title="Organization Settings">
        <Banner tone="warning">
          Please select an organization to manage settings.
        </Banner>
      </Page>
    );
  }

  return (
    <Page
      title="Organization Settings"
      subtitle={org?.name || currentOrganization.name}
      backAction={{ content: 'Settings', onAction: () => navigate('/settings') }}
    >
      <BlockStack gap="600">
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}

        {success && (
          <Banner tone="success" onDismiss={() => setSuccess(null)}>
            {success}
          </Banner>
        )}

        <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
          <Box paddingBlockStart="400">
            {/* General Tab */}
            {selectedTab === 0 && (
              <Card>
                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Organization Details
                  </Text>

                  <TextField
                    label="Organization Name"
                    requiredIndicator value={name}
                    onChange={setName}
                    disabled={isLoading}
                    autoComplete="organization"
                    helpText="The display name for your organization"
                  />

                  <TextField
                    label="URL Slug"
                    requiredIndicator value={slug}
                    disabled
                    autoComplete="off"
                    prefix="https://"
                    suffix=".whenlyapp.com"
                    helpText="Company address. Contact support to change this address."
                  />

                  {org && (
                    <>
                      <Divider />
                      <InlineStack gap="800">
                        <BlockStack gap="100">
                          <Text as="span" variant="bodySm" tone="subdued">Team Members</Text>
                          <Text as="span" variant="headingMd">{org.memberCount}</Text>
                        </BlockStack>
                        <BlockStack gap="100">
                          <Text as="span" variant="bodySm" tone="subdued">Connected Stores</Text>
                          <Text as="span" variant="headingMd">{org.shopCount}</Text>
                        </BlockStack>
                        <BlockStack gap="100">
                          <Text as="span" variant="bodySm" tone="subdued">Created</Text>
                          <Text as="span" variant="bodyMd">
                            {new Date(org.createdAt).toLocaleDateString()}
                          </Text>
                        </BlockStack>
                      </InlineStack>
                    </>
                  )}

                  <InlineStack align="end">
                    <Button validate
                      variant="primary"
                      onClick={handleSaveGeneral}
                      loading={isSaving}
                      disabled={isLoading}
                    >
                      Save Changes
                    </Button>
                  </InlineStack>
                </BlockStack>
              </Card>
            )}

            {selectedTab === 1 && <CompanyAuthPanel key={currentOrganization.id} />}

            {/* Team Tab */}
            {selectedTab === 2 && (
              <Card>
                <BlockStack gap="400">
                  <InlineStack align="space-between">
                    <Text as="h2" variant="headingMd">
                      Team Members
                    </Text>
                    <Button
                      variant="primary"
                      onClick={() => navigate('/settings/organization/team')}
                    >
                      Manage Team
                    </Button>
                  </InlineStack>
                  <Text as="p" variant="bodyMd" tone="subdued">
                    View and manage your organization's team members, roles, and invitations.
                  </Text>
                  <Button onClick={() => navigate('/settings/organization/team')}>
                    Go to Team Management
                  </Button>
                </BlockStack>
              </Card>
            )}
          </Box>
        </Tabs>
      </BlockStack>
    </Page>
  );
}
